import { useUser } from "@clerk/clerk-react";
import { useAuth } from "@clerk/clerk-react";
import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  FileUp,
  Menu,
  X,
  Search,
  Plus,
  Edit,
  Trash2,
  Maximize,
  ChevronLeft,
  File,
  FileText as FileTextIcon,
  Download,
  MoreVertical,
  User,
  PenTool,
  Calendar,
  Clock,
  ExternalLink,
  Sparkles,
  Bookmark,
  Star,
  BookOpen,
  Command,
  Sun,
  Moon,
  ChevronRight,
  Activity,
  Layers,
  Users,
  Lock,
  Pencil,
  Send,
  MessageCircle,
} from "lucide-react";
import Profile from "../pages/Profile";
// Import WelcomeMessage, but remove ChatWidget since we'll create our own
import WelcomeMessage from "../pages/WelcomeMessage";

const url = process.env.REACT_APP_BACK_END_URL;

// Get appropriate file icon based on extension (Ultra Modern Theme)
const getFileIcon = (filename) => {
  const ext = filename.split(".").pop().toLowerCase();
  const iconClass = "w-5 h-5 group-hover:scale-110 transition-transform";
  const icons = {
    pdf: <FileTextIcon size={18} className={`${iconClass} text-red-400`} />,
    doc: <FileTextIcon size={18} className={`${iconClass} text-blue-400`} />,
    docx: <FileTextIcon size={18} className={`${iconClass} text-blue-400`} />,
    xls: <FileTextIcon size={18} className={`${iconClass} text-green-400`} />,
    xlsx: <FileTextIcon size={18} className={`${iconClass} text-green-400`} />,
    png: <FileTextIcon size={18} className={`${iconClass} text-purple-400`} />,
    jpg: <FileTextIcon size={18} className={`${iconClass} text-purple-400`} />,
    default: <File size={18} className={`${iconClass} text-gray-500`} />
  };
  return (
    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500/10 to-indigo-500/10 dark:from-violet-500/5 dark:to-indigo-500/5 flex items-center justify-center transition-all duration-300 group-hover:bg-gradient-to-br group-hover:from-violet-500/20 group-hover:to-indigo-500/20">
      {icons[ext] || icons.default}
    </div>
  );
};

// Animated background for welcome screen (simplified for better performance)
const AnimatedBackground = () => (
  <div className="absolute inset-0 overflow-hidden -z-10">
    <div className="absolute top-1/4 left-1/5 w-72 h-72 bg-violet-600/10 rounded-full blur-3xl animate-float-slow"></div>
    <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-indigo-600/10 rounded-full blur-3xl animate-float-medium"></div>
    <div className="absolute bottom-1/4 left-1/3 w-72 h-72 bg-fuchsia-600/10 rounded-full blur-3xl animate-float-fast"></div>
    <style jsx>{`
      @keyframes float-slow {
        0%, 100% { transform: translate(0, 0) rotate(0deg); }
        33% { transform: translate(30px, -50px) rotate(10deg); }
        66% { transform: translate(-20px, 40px) rotate(-5deg); }
      }
      @keyframes float-medium {
        0%, 100% { transform: translate(0, 0) rotate(0deg); }
        33% { transform: translate(-40px, 40px) rotate(-10deg); }
        66% { transform: translate(30px, -30px) rotate(5deg); }
      }
      @keyframes float-fast {
        0%, 100% { transform: translate(0, 0) rotate(0deg); }
        33% { transform: translate(20px, 30px) rotate(5deg); }
        66% { transform: translate(-40px, -20px) rotate(-10deg); }
      }
      .animate-float-slow { animation: float-slow 20s ease-in-out infinite; }
      .animate-float-medium { animation: float-medium 15s ease-in-out infinite; }
      .animate-float-fast { animation: float-fast 12s ease-in-out infinite; }
    `}</style>
  </div>
);

// Glass Morphism Card component (Improved contrast for better readability)
const Card = ({ children, className = "", expandable = false, featured = false }) => (
  <div
    className={`group relative bg-white/20 dark:bg-black/40 backdrop-blur-xl border border-white/30 dark:border-white/10 rounded-2xl overflow-hidden transition-all duration-500 hover:shadow-[0_0_30px_rgba(139,92,246,0.2)] dark:hover:shadow-[0_0_30px_rgba(139,92,246,0.3)] dark:hover:border-violet-500/50 hover:border-violet-500/50 ${
      expandable ? "cursor-pointer hover:-translate-y-1" : ""
    } ${featured ? "ring-2 ring-violet-500/70 dark:ring-violet-500/50 ring-offset-2 ring-offset-gray-100 dark:ring-offset-gray-950" : ""} ${className}`}
  >
    <div className="absolute inset-0 bg-gradient-to-br from-violet-600/10 to-indigo-600/10 dark:from-violet-600/5 dark:to-indigo-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
    <div className="absolute -inset-1 bg-gradient-to-br from-violet-600/20 to-indigo-600/20 rounded-xl blur-xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 -z-10"></div>
    <div className="relative">{children}</div>
  </div>
);

const CardContent = ({ children, className = "" }) => (
  <div className={`p-6 ${className}`}>{children}</div>
);

// Modern button with 3D hover effect (Ultra Modern Theme)
const Button = ({ 
  children, 
  onClick, 
  variant = "primary", 
  size = "md", 
  className = "", 
  icon,
  disabled,
  ...props 
}) => {
  const baseClasses = "relative font-medium rounded-xl transition-all duration-300 transform active:scale-95 active:translate-y-0.5 overflow-hidden flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-950 focus:ring-violet-500";
  
  const sizeClasses = {
    sm: "px-4 py-2 text-sm",
    md: "px-6 py-2.5",
    lg: "px-8 py-3.5 text-lg",
  };

  let variantClasses = "";
  if (variant === "primary") {
    variantClasses = "bg-gradient-to-br from-violet-600 to-indigo-600 text-white shadow-[0_6px_0_rgb(76,29,149)] active:shadow-[0_3px_0_rgb(76,29,149)] hover:from-violet-500 hover:to-indigo-500";
  } else if (variant === "secondary") {
    variantClasses = "bg-white/80 dark:bg-gray-800/80 text-gray-800 dark:text-white shadow-[0_6px_0_rgb(209,213,219)] dark:shadow-[0_6px_0_rgb(31,41,55)] active:shadow-[0_3px_0_rgb(209,213,219)] dark:active:shadow-[0_3px_0_rgb(31,41,55)] hover:bg-gray-50 dark:hover:bg-gray-700/80 border border-gray-300 dark:border-gray-700";
  } else if (variant === "glass") {
    variantClasses = "backdrop-blur-md bg-white/20 border border-white/30 text-white shadow-[0_6px_0_rgba(255,255,255,0.1)] active:shadow-[0_3px_0_rgba(255,255,255,0.1)] hover:bg-white/30";
  } else if (variant === "danger") {
    variantClasses = "bg-gradient-to-br from-red-500 to-pink-500 text-white shadow-[0_6px_0_rgb(159,18,57)] active:shadow-[0_3px_0_rgb(159,18,57)] hover:from-red-400 hover:to-pink-400";
  } else { // ghost as default/fallback
    variantClasses = "bg-transparent text-gray-400 dark:text-gray-300 hover:bg-violet-500/10 hover:text-violet-400 dark:hover:text-violet-300 rounded-lg shadow-none active:translate-y-0";
  }
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${sizeClasses[size]} ${variantClasses} ${className} ${disabled ? 'opacity-60 cursor-not-allowed !shadow-none active:!translate-y-0' : ''}`}
      {...props}
    >
      <div className={`absolute inset-0 ${variant === "primary" || variant === "danger" ? "bg-white/20" : "bg-black/10"} opacity-0 hover:opacity-100 transition-opacity duration-300`}></div>
      <div className="relative z-10 flex items-center justify-center">
        {icon && <span className="mr-2">{icon}</span>}
        {children}
      </div>
    </button>
  );
};

// Icon button with tooltip (Ultra Modern Theme)
const IconButton = ({ 
  icon, 
  onClick, 
  tooltip = "", 
  className = "",
  variant = "ghost", // primary, secondary, glass, ghost
  size = "md", // sm, md
  disabled,
  ...props 
}) => {
  const baseIconBtnClasses = "relative group p-2.5 rounded-xl transition-all duration-300 transform active:scale-90 active:translate-y-0.5 overflow-hidden flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-gray-950 focus:ring-violet-500";

  const sizeClasses = {
    sm: "p-2",
    md: "p-2.5",
  };
  
  let variantClasses = "";
  if (variant === "primary") {
    variantClasses = "bg-gradient-to-br from-violet-600 to-indigo-600 text-white shadow-[0_4px_0_rgb(76,29,149)] active:shadow-[0_2px_0_rgb(76,29,149)] hover:from-violet-500 hover:to-indigo-500";
  } else if (variant === "secondary") {
    variantClasses = "bg-white/80 dark:bg-gray-800/80 text-gray-700 dark:text-white shadow-[0_4px_0_rgb(209,213,219)] dark:shadow-[0_4px_0_rgb(31,41,55)] active:shadow-[0_2px_0_rgb(209,213,219)] dark:active:shadow-[0_2px_0_rgb(31,41,55)] hover:bg-gray-50 dark:hover:bg-gray-700/80 border border-gray-300 dark:border-gray-700";
  } else if (variant === "glass") {
     variantClasses = "backdrop-blur-md bg-white/20 border border-white/30 text-white shadow-[0_4px_0_rgba(255,255,255,0.1)] active:shadow-[0_2px_0_rgba(255,255,255,0.1)] hover:bg-white/30";
  } else { // ghost
    variantClasses = "text-gray-200 dark:text-gray-300 hover:bg-violet-500/20 hover:text-violet-400 dark:hover:text-violet-300 shadow-none active:translate-y-0 rounded-lg";
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseIconBtnClasses} ${sizeClasses[size]} ${variantClasses} ${className} ${disabled ? 'opacity-60 cursor-not-allowed !shadow-none active:!translate-y-0' : ''}`}
      {...props}
    >
       <div className={`absolute inset-0 ${variant === "primary" ? "bg-white/20" : "bg-black/10"} opacity-0 hover:opacity-100 transition-opacity duration-300`}></div>
      <div className="relative z-10">{icon}</div>
      {tooltip && (
        <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none transition-opacity duration-300 shadow-lg">
          {tooltip}
        </div>
      )}
    </button>
  );
};

// File upload preview component (Ultra Modern Theme)
const FileUploadPreview = ({ file, onRemove }) => {
  return (
    <div className="group relative flex items-center gap-4 p-4 bg-white/10 dark:bg-black/30 backdrop-blur-md border border-white/20 dark:border-white/10 rounded-xl transition-all duration-300 hover:border-violet-500/30 hover:bg-white/20 dark:hover:bg-black/40">
      {getFileIcon(file.name)}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-200 dark:text-gray-100 truncate group-hover:text-violet-300 transition-colors">{file.name}</p>
        <p className="text-xs text-gray-400 dark:text-gray-400">
          {(file.size / 1024 / 1024).toFixed(2)} MB
        </p>
      </div>
      <IconButton
        variant="ghost"
        size="sm"
        onClick={onRemove}
        className="opacity-50 group-hover:opacity-100 !text-red-400 hover:!bg-red-500/10"
        icon={<X size={16} />}
        tooltip="Remove"
      />
    </div>
  );
};

// Empty state component (Ultra Modern Theme)
const EmptyState = ({ title, description, action, icon }) => (
  <div className="flex flex-col items-center justify-center p-10 text-center animate-fadeInUp">
    <div className="w-20 h-20 mb-6 flex items-center justify-center rounded-full bg-gradient-to-br from-violet-500/20 to-indigo-500/20 dark:from-violet-500/10 dark:to-indigo-500/10 ring-4 ring-white/10 dark:ring-black/20">
       <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center text-white shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
        {React.cloneElement(icon, { size: 32, strokeWidth: 1.5 })}
       </div>
    </div>
    <h3 className="text-2xl font-bold text-white mb-3 transition-colors duration-500 group-hover:text-violet-400">{title}</h3>
    <p className="text-gray-300 dark:text-gray-300 leading-relaxed mb-8 max-w-md">{description}</p>
    {action}
  </div>
);


const NoteApp = () => {
  // State declarations (no change here, just for context)
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState("default");
  const [currentNote, setCurrentNote] = useState({
    id: null,
    title: "",
    content: "",
    attachments: [],
  });
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [newPageTitle, setNewPageTitle] = useState("");
  const [isAddingPage, setIsAddingPage] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [editingPageId, setEditingPageId] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(true); // Default to dark to match landing
  const [attachments, setAttachments] = useState([]);
  const [showProfile, setShowProfile] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // State for chat functionality
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessage, setChatMessage] = useState("");
  const [chatMessages, setChatMessages] = useState([
    { sender: "bot", text: "Hi there! How can I help you with your notes today?" }
  ]);
  
  const quillRef = useRef(null);
  const fileInputRef = useRef(null);
  const profilePanelRef = useRef(null);
  const chatInputRef = useRef(null);

  const { user } = useUser();
  const userId = user ? user.id : null;
  const { getToken } = useAuth();

  // Add a function to handle sending chat messages
  const handleSendMessage = () => {
    if (chatMessage.trim() === "") return;
    
    // Add user message to chat
    setChatMessages(prev => [...prev, { sender: "user", text: chatMessage }]);
    
    // Simulate AI response
    setTimeout(() => {
      const botResponses = [
        "I can help you organize your notes better. Would you like some tips?",
        "You can use the search feature to find specific notes quickly.",
        "Try adding tags to your notes for better organization.",
        "Did you know you can upload attachments to your notes?",
        "I'm here to help! Just let me know what you need."
      ];
      const randomResponse = botResponses[Math.floor(Math.random() * botResponses.length)];
      setChatMessages(prev => [...prev, { sender: "bot", text: randomResponse }]);
    }, 1000);
    
    // Clear input
    setChatMessage("");
    // Focus input field again
    if (chatInputRef.current) {
      chatInputRef.current.focus();
    }
  };

  // Check for dark mode preference (prioritize saved, then system, then default to true)
  useEffect(() => {
    const savedMode = localStorage.getItem('darkMode');
    if (savedMode !== null) {
      setIsDarkMode(savedMode === 'true');
    } else {
      const prefersDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkMode(prefersDarkMode); // Use system preference if no saved mode
    }
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      if (localStorage.getItem('darkMode') === null) { // Only if user hasn't manually set it
        setIsDarkMode(e.matches);
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);
  
  // Apply dark mode to body
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', isDarkMode.toString());
  }, [isDarkMode]);

  // Add click outside handler for profile panel
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profilePanelRef.current && !profilePanelRef.current.contains(event.target)) {
        setShowProfile(false);
      }
    };

    if (showProfile) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfile]);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    setIsLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(url + "/api/pages", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const sortedPages = data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setPages(sortedPages);
        // If no current page or default, and pages exist, set to first page
        if ((currentPage === "default" || !pages.find(p => p._id === currentPage)) && sortedPages.length > 0) {
          setCurrentPage(sortedPages[0]._id);
        } else if (sortedPages.length === 0) {
          setCurrentPage("default"); // Ensure default if no pages
        }
      } else {
        console.error("Failed to fetch pages");
        setPages([]); // Clear pages on error
        setCurrentPage("default");
      }
    } catch (error) {
      console.error("Error fetching pages:", error);
      setPages([]);
      setCurrentPage("default");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
  };
  
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: 'numeric' }).format(date);
  };

  const currentPageData = pages.find((page) => page._id === currentPage) || { notes: [], title: "Notebook", createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };

  const sortedNotes = currentPageData.notes?.slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const filteredNotes = sortedNotes?.filter(
    (note) =>
      (note.title && note.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (note.content && note.content.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter((file) => {
      const ext = file.name.split(".").pop().toLowerCase();
      const validExtensions = ["pdf", "doc", "docx", "xls", "xlsx", "png", "jpg", "jpeg", "gif", "txt"]; // Expanded
      const maxSize = 15 * 1024 * 1024; // 15MB limit

      if (!validExtensions.includes(ext)) {
        alert(`Invalid file type: ${file.name}.`);
        return false;
      }
      if (file.size > maxSize) {
        alert(`File too large: ${file.name}. Maximum size is 15MB.`);
        return false;
      }
      return true;
    });
    setAttachments((prev) => [...prev, ...validFiles]);
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteNote = async (noteId) => {
    // ... (backend logic unchanged)
    try {
      const token = await getToken();
      const response = await fetch(url + `/api/notes/${noteId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (response.ok) {
        setPages(pages.map((page) => {
          if (page._id === currentPage) {
            return { ...page, notes: page.notes.filter((note) => note._id !== noteId) };
          }
          return page;
        }));
      } else { console.error("Failed to delete note"); }
    } catch (error) { console.error("Error deleting note:", error); }
  };

  const handleDeletePage = async (pageId) => {
    // ... (backend logic unchanged)
    try {
      const token = await getToken();
      const response = await fetch(url + `/api/pages/${pageId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (response.ok) {
        const updatedPages = pages.filter((page) => page._id !== pageId);
        setPages(updatedPages);
        if (currentPage === pageId) { // If current page was deleted
          setCurrentPage(updatedPages.length > 0 ? updatedPages[0]._id : "default");
        }
      } else { console.error("Failed to delete page"); }
    } catch (error) { console.error("Error deleting page:", error); }
  };

  const handleAddPage = async () => {
    // ... (backend logic unchanged)
    if (!newPageTitle.trim()) return;
    try {
      const token = await getToken();
      const response = await fetch(url + "/api/pages", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ userId: userId, title: newPageTitle }),
      });
      if (response.ok) {
        const newPage = await response.json();
        // Instead of full fetchPages, optimistically update
        setPages(prevPages => [newPage, ...prevPages].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
        setCurrentPage(newPage._id);
        setNewPageTitle("");
        setIsAddingPage(false);
      } else { console.error("Failed to add page"); }
    } catch (error) { console.error("Error adding page:", error); }
  };

  const handleRenamePage = async (pageId, newTitle) => {
    // ... (backend logic unchanged)
     if (!newTitle.trim()) return;
    try {
      const token = await getToken();
      const response = await fetch(url + `/api/pages/${pageId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ title: newTitle }),
      });
      if (response.ok) {
        const updatedPage = await response.json();
        setPages(pages.map((page) => page._id === pageId ? { ...page, title: updatedPage.title, updatedAt: updatedPage.updatedAt } : page));
        setEditingPageId(null);
      } else { console.error("Failed to update page title"); }
    } catch (error) { console.error("Error updating page title:", error); }
  };

  const handleSave = async () => {
    // ... (backend logic unchanged, ensure formData matches backend expectations)
    if (!currentNote.title || !currentNote.content) return;
    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append("userId", userId);
      formData.append("pageId", currentPage);
      formData.append("title", currentNote.title);
      formData.append("content", currentNote.content);
      formData.append("format", "html");
      if (attachments && attachments.length > 0) {
        attachments.forEach((file) => formData.append("attachments", file));
      }
      const response = await fetch(url + "/api/notes", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (response.ok) {
        const savedNote = await response.json();
        setPages(pages.map((page) => {
          if (page._id === currentPage) {
            return { ...page, notes: [savedNote, ...page.notes] }; // Add to top
          }
          return page;
        }));
        setCurrentNote({ id: null, title: "", content: "", attachments: [] });
        setAttachments([]);
        setIsEditing(false);
      } else { console.error("Failed to save note"); }
    } catch (error) { console.error("Error saving note:", error); }
  };

  const handleUpdate = async (noteId) => {
    // ... (backend logic unchanged)
    if (!currentNote.title || !currentNote.content) return;
    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append("title", currentNote.title);
      formData.append("content", currentNote.content);
      formData.append("format", "html");
      // Handle new attachments for update
      if (attachments && attachments.length > 0) {
        attachments.forEach((file) => formData.append("attachments", file));
      }
      const response = await fetch(url + `/api/notes/${noteId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (response.ok) {
        const updatedNote = await response.json();
        setPages(pages.map((page) => {
          if (page._id === currentPage) {
            return { ...page, notes: page.notes.map((note) => note._id === noteId ? updatedNote : note) };
          }
          return page;
        }));
        setCurrentNote({ id: null, title: "", content: "", attachments: [] });
        setAttachments([]);
        setIsEditing(false);
      } else { console.error("Failed to update note"); }
    } catch (error) { console.error("Error updating note:", error); }
  };

  const handleDeleteAttachment = async (noteId, filename) => {
    // ... (backend logic unchanged)
    try {
      const token = await getToken();
      const response = await fetch(`${url}/api/notes/${noteId}/attachments/${filename}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (response.ok) {
        setPages((prevPages) => prevPages.map((page) => {
          if (page._id === currentPage) {
            return { ...page, notes: page.notes.map((note) => {
              if (note._id === noteId) {
                return { ...note, attachments: note.attachments.filter((att) => att.filename !== filename) };
              }
              return note;
            })};
          }
          return page;
        }));
        if (currentNote._id === noteId) { // If editing the note whose attachment was deleted
          setCurrentNote(prev => ({ ...prev, attachments: prev.attachments.filter(att => att.filename !== filename)}));
        }
      } else { console.error("Failed to delete attachment"); }
    } catch (error) { console.error("Error deleting attachment:", error); }
  };

  const NoteContent = ({ noteId, content, attachments, onDeleteAttachment }) => {
    const renderAttachments = () => {
      if (!attachments || attachments.length === 0) return null;
      return (
        <div className="mt-8 pt-6 border-t border-white/20">
          <h3 className="text-sm font-semibold text-violet-300 mb-4 tracking-wider">ATTACHMENTS</h3>
          <div className="space-y-3">
            {attachments.map((file, index) => (
              <div key={index} className="group relative flex items-center gap-4 p-3 bg-black/30 backdrop-blur-sm border border-white/20 rounded-xl hover:border-violet-500/30">
                {getFileIcon(file.originalName)}
                <div className="flex-1 min-w-0">
                  <a
                    href={`${url}/uploads/${file.filename}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-gray-200 hover:text-violet-300 truncate hover:underline"
                    download={file.originalName}
                  >
                    {file.originalName}
                  </a>
                  <p className="text-xs text-gray-400">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
                <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <IconButton
                    icon={<Download size={16} />} tooltip="Download"
                    onClick={() => { /* ... download logic ... */ }}
                    variant="ghost" size="sm" className="!text-gray-300 hover:!text-violet-300"
                  />
                  {onDeleteAttachment && (
                    <IconButton
                      icon={<Trash2 size={16} />} tooltip="Delete"
                      onClick={() => onDeleteAttachment(noteId, file.filename)}
                      variant="ghost" size="sm" className="!text-red-400 hover:!bg-red-500/20"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    };
    return (
      <div>
        <div
          className="prose prose-sm dark:prose-invert max-w-none 
            prose-headings:font-semibold prose-headings:text-violet-300 prose-headings:tracking-tight
            prose-p:text-gray-300 prose-li:text-gray-300
            prose-a:text-violet-400 hover:prose-a:text-violet-300
            prose-strong:text-gray-100
            prose-code:bg-black/30 prose-code:p-1 prose-code:rounded-md prose-code:text-sm prose-code:text-violet-300 prose-code:font-mono
            [&_pre]:bg-black/40 [&_pre]:border [&_pre]:border-white/20 [&_pre]:rounded-lg [&_pre]:p-4 [&_pre]:text-sm
            [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
          dangerouslySetInnerHTML={{ __html: content }}
        />
        {renderAttachments()}
      </div>
    );
  };

  const SidebarLink = ({ page, isActive, onClick }) => (
    <div
      className={`group relative flex items-center justify-between p-2.5 rounded-xl cursor-pointer transition-all duration-300 transform hover:-translate-x-0.5 hover:shadow-lg
        ${ isActive
          ? "bg-gradient-to-r from-violet-600/30 to-indigo-600/30 text-white shadow-violet-500/20"
          : "bg-white/10 dark:bg-black/30 hover:bg-white/20 dark:hover:bg-black/40 text-gray-300 dark:text-gray-300 hover:text-violet-300 dark:hover:text-violet-400"
      }`}
      onClick={onClick}
    >
      <div className="absolute -inset-0.5 bg-gradient-to-r from-violet-600 to-indigo-600 rounded-xl blur opacity-0 group-hover:opacity-75 transition duration-500 group-hover:duration-200 animate-tilt"></div>
      <div className="relative flex items-center gap-3 flex-1 min-w-0">
        <div className={`p-2 rounded-lg ${isActive ? "bg-white/20" : "bg-white/10 dark:bg-black/40 group-hover:bg-violet-500/20"} transition-colors`}>
          <BookOpen size={16} className={isActive ? "text-white" : "text-violet-400 dark:text-violet-500 group-hover:text-violet-300"} />
        </div>
        <span className={`flex-1 truncate font-medium text-sm ${isActive ? "text-white" : ""}`}>{page.title}</span>
      </div>
      <div className="relative z-10 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <IconButton
          icon={<Edit size={14} />} tooltip="Rename"
          onClick={(e) => { e.stopPropagation(); setEditingPageId(page._id); }}
          variant="ghost" size="sm" className="!p-1.5 !text-gray-300 hover:!text-violet-300"
        />
        {pages.length > 1 && (
          <IconButton
            icon={<Trash2 size={14} />} tooltip="Delete"
            onClick={(e) => { e.stopPropagation(); handleDeletePage(page._id); }}
            variant="ghost" size="sm" className="!p-1.5 !text-red-400 hover:!bg-red-500/10"
          />
        )}
      </div>
    </div>
  );


  return (
    <div className={`min-h-screen bg-gray-900 dark:bg-gray-950 text-gray-200 dark:text-gray-200 transition-colors duration-200 font-sans`}>
      {!isSidebarOpen && (
        <IconButton
          icon={<Menu size={20} />}
          tooltip="Open sidebar"
          onClick={() => setIsSidebarOpen(true)}
          variant="glass"
          className="!fixed left-5 top-5 z-40 !rounded-full !p-3"
        />
      )}

      <div
        className={`${ isSidebarOpen ? "translate-x-0" : "-translate-x-full" }
        fixed top-0 left-0 z-30 h-screen w-72 transform transition-transform duration-300 ease-in-out 
        bg-black/60 dark:bg-black/70 backdrop-blur-2xl border-r border-white/20 dark:border-white/10 shadow-2xl overflow-hidden flex flex-col`}
      >
        <div className="p-5 border-b border-white/20">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3 group">
              <div className="relative">
                <div className="absolute -inset-2 bg-gradient-to-r from-violet-600 to-indigo-600 rounded-full blur-lg opacity-75 group-hover:opacity-100 transition-opacity duration-300 animate-pulse-gentle"></div>
                <div className="relative bg-black/50 rounded-full p-2.5 border border-white/20 shadow-md">
                  <Pencil className="h-5 w-5 text-violet-400 transition-all duration-300 group-hover:rotate-12" />
                </div>
              </div>
              <h2 className="text-2xl font-bold tracking-tight text-gradient">Noted</h2>
            </div>
            <IconButton
              icon={<ChevronLeft size={20} />}
              tooltip="Collapse sidebar"
              onClick={() => setIsSidebarOpen(false)}
              variant="ghost"
              className="!text-gray-300 hover:!text-white"
            />
          </div>
        </div>
        
        <div className="p-4 flex-1 overflow-y-auto space-y-6 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
            <div className="flex justify-between items-center">
              <h3 className="text-xs font-semibold text-gray-300 dark:text-gray-300 uppercase tracking-wider">Notebooks</h3>
              <Button
                variant="glass"
                size="sm"
                onClick={() => setIsAddingPage(true)}
                icon={<Plus size={14} />}
                className="!py-1.5 !px-3 !text-xs !shadow-[0_4px_0_rgba(255,255,255,0.05)] active:!shadow-[0_2px_0_rgba(255,255,255,0.05)]"
              >
                New
              </Button>
            </div>
            
            {isAddingPage && (
              <div className="mb-4 bg-black/30 dark:bg-black/40 p-4 rounded-xl border border-white/20">
                <input
                  type="text" value={newPageTitle} onChange={(e) => setNewPageTitle(e.target.value)}
                  placeholder="Notebook name..."
                  className="w-full p-2.5 mb-3 bg-black/30 dark:bg-black/20 border border-white/20 dark:border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-white placeholder-gray-400"
                  autoFocus
                />
                <div className="flex space-x-2">
                  <Button variant="primary" size="sm" onClick={handleAddPage} className="flex-1 !py-2">Create</Button>
                  <Button variant="secondary" size="sm" onClick={() => setIsAddingPage(false)} className="flex-1 !py-2 !bg-white/10 hover:!bg-white/20 !text-gray-200 !shadow-[0_4px_0_rgba(255,255,255,0.05)] active:!shadow-[0_2px_0_rgba(255,255,255,0.05)]">Cancel</Button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {isLoading ? (
                [...Array(3)].map((_, i) => (
                  <div key={i} className="p-2.5 animate-pulse bg-black/30 rounded-xl">
                    <div className="flex items-center space-x-3">
                      <div className="h-8 w-8 bg-white/10 rounded-md"></div>
                      <div className="h-4 bg-white/10 rounded w-3/4"></div>
                    </div>
                  </div>
                ))
              ) : pages.length > 0 ? (
                pages.map((page) => (
                  <React.Fragment key={page._id}>
                    {editingPageId === page._id ? (
                      <div className="p-2.5 bg-black/40 rounded-xl">
                        <input
                          type="text" defaultValue={page.title}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => { if (e.key === "Enter") handleRenamePage(page._id, e.target.value); else if (e.key === "Escape") setEditingPageId(null);}}
                          onBlur={(e) => handleRenamePage(page._id, e.target.value)}
                          className="w-full p-2 bg-black/20 border border-white/20 rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500 text-white"
                          autoFocus
                        />
                      </div>
                    ) : (
                      <SidebarLink
                        page={page} isActive={currentPage === page._id}
                        onClick={() => { setCurrentPage(page._id); if (window.innerWidth < 768) setIsSidebarOpen(false); }}
                      />
                    )}
                  </React.Fragment>
                ))
              ) : (
                !isAddingPage && <EmptyState title="No Notebooks" description="Create your first notebook to organize your thoughts." icon={<BookOpen />}
                  action={ <Button variant="primary" size="sm" onClick={() => setIsAddingPage(true)} icon={<Plus size={16} />}>New Notebook</Button> }
                />
              )}
            </div>
          </div>

        <div className="p-4 border-t border-white/20 mt-auto">
            <div className="flex justify-between items-center">
                <button onClick={() => setShowProfile(true)} className="group flex items-center space-x-3 p-2 rounded-lg hover:bg-white/10 w-full text-left transition-colors">
                    <div className="relative">
                        <img src={user?.imageUrl} alt={user?.fullName || "P"} className="w-10 h-10 rounded-full object-cover ring-2 ring-black/30 group-hover:ring-violet-500/50 transition-all" />
                        <div className="absolute bottom-0 right-0 h-3 w-3 bg-green-500 rounded-full ring-2 ring-black/50 border border-white/20"></div>
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white truncate">{user?.fullName}</p>
                        <p className="text-xs text-gray-400 dark:text-gray-400 truncate">{user?.primaryEmailAddress?.emailAddress}</p>
                    </div>
                </button>
                <IconButton
                    icon={isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
                    tooltip={isDarkMode ? "Light Mode" : "Dark Mode"}
                    onClick={() => setIsDarkMode(!isDarkMode)}
                    variant="glass"
                    className="!p-3"
                />
            </div>
        </div>
      </div>

      <div className={`transition-all duration-300 ${isSidebarOpen ? "md:ml-72" : "ml-0"}`}>
        <div className="min-h-screen max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {currentPage === "default" && !isLoading ? (
            <div className="relative flex flex-col items-center justify-center min-h-[calc(100vh-10rem)] text-center">
              <AnimatedBackground />
              <WelcomeMessage />
            </div>
          ) : isLoading && currentPage === "default" ? (
             <div className="flex items-center justify-center min-h-[calc(100vh-10rem)]">
                <svg className="animate-spin h-10 w-10 text-violet-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
             </div>
          ) : (
            <>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
                <div className="animate-fadeInUp">
                  <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gradient mb-1">
                    {currentPageData?.title || "Notes"}
                  </h1>
                  <p className="text-sm text-gray-300 dark:text-gray-300 flex items-center">
                    <Calendar size={14} className="mr-1.5" />
                    Created: {formatDate(currentPageData?.createdAt)}
                    <span className="mx-2 opacity-50">•</span>
                    <Clock size={14} className="mr-1.5" />
                    Updated: {formatDate(currentPageData?.updatedAt)}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-3 animate-fadeInUp" style={{animationDelay: '0.1s'}}>
                  <div className={`relative transition-all duration-300 ${isSearchFocused ? "w-60" : "w-48"}`}>
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none">
                      <Search size={16} className="text-gray-400" />
                    </div>
                    <input
                      type="text" placeholder="Search notes..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => setIsSearchFocused(true)} onBlur={() => setIsSearchFocused(false)}
                      className="w-full py-2.5 pl-10 pr-10 bg-white/10 dark:bg-black/30 backdrop-blur-sm border border-white/20 dark:border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/70 focus:border-violet-500 text-sm text-white placeholder-gray-400 transition-all"
                    />
                    {searchQuery && (
                      <button onClick={() => setSearchQuery("")} className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-violet-400">
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  
                  <IconButton icon={<User size={18} />} tooltip="Profile" onClick={() => setShowProfile(true)} variant="glass" className="!p-3" />
                  
                  {!isEditing && (
                    <Button variant="primary" onClick={() => { setIsEditing(true); setCurrentNote({ id: null, title: "", content: "", attachments: [] }); }} icon={<Plus size={18} />}>
                      New Note
                    </Button>
                  )}
                </div>
              </div>
              
              {isEditing && (
                <Card className="mb-10 backdrop-blur-lg border-violet-500/30 dark:border-violet-500/20 shadow-xl animate-fadeInUp" style={{animationDelay: '0.2s'}}>
                  <div className="px-6 py-4 border-b border-white/20 dark:border-white/10">
                    <h2 className="text-xl font-semibold text-white">
                      {currentNote._id ? "Edit Note" : "Create New Note"}
                    </h2>
                  </div>
                  <CardContent className="pt-6 pb-4">
                    <input
                      type="text" value={currentNote.title} onChange={(e) => setCurrentNote({ ...currentNote, title: e.target.value })}
                      placeholder="Untitled Note..."
                      className="w-full text-3xl font-bold mb-6 p-2 bg-transparent border-b border-white/20 focus:border-violet-400 focus:outline-none transition-colors text-white placeholder:text-gray-500"
                    />
                    
                    <div className="mb-6 prose-container rounded-xl overflow-hidden border border-white/20">
                      <ReactQuill
                        ref={quillRef} value={currentNote.content} onChange={(content) => setCurrentNote({ ...currentNote, content })}
                        modules={{ toolbar: [ /* Toolbar options */ ]}} formats={[ /* Formats */ ]} theme="snow"
                        className="min-h-[250px]"
                      />
                    </div>

                    {currentNote.attachments && currentNote.attachments.length > 0 && (
                      <div className="my-6 p-4 bg-black/30 rounded-xl border border-white/20">
                        <h3 className="text-sm font-semibold text-violet-300 mb-3 tracking-wider">EXISTING ATTACHMENTS</h3>
                        <div className="space-y-2">
                          {currentNote.attachments.map((file, index) => (
                             <div key={index} className="group relative flex items-center gap-4 p-3 bg-black/40 border border-white/20 rounded-xl">
                                {getFileIcon(file.originalName)}
                                <div className="flex-1 min-w-0">
                                  <a href={`${url}/uploads/${file.filename}`} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-gray-200 hover:text-violet-300 truncate" download={file.originalName}>{file.originalName}</a>
                                  <p className="text-xs text-gray-400">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                                </div>
                                <IconButton variant="ghost" size="sm" onClick={() => handleDeleteAttachment(currentNote._id, file.filename)} icon={<Trash2 size={16} />} className="!text-red-400 hover:!bg-red-500/10 opacity-50 group-hover:opacity-100" tooltip="Remove"/>
                              </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="my-6 p-4 bg-black/30 rounded-xl border border-white/20">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-violet-300 tracking-wider">ADD ATTACHMENTS</h3>
                        <Button variant="glass" size="sm" onClick={() => fileInputRef.current?.click()} icon={<FileUp size={14} />} className="!py-1.5 !text-xs">Upload</Button>
                      </div>
                      <input type="file" ref={fileInputRef} onChange={handleFileUpload} multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.txt" className="hidden"/>
                      <div className="space-y-3 mt-2">
                        {attachments.length > 0 ? attachments.map((file, index) => ( <FileUploadPreview key={index} file={file} onRemove={() => removeAttachment(index)} /> ))
                         : <div className="text-center py-4 text-gray-400 text-sm">Drag files or click "Upload"</div>}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3 mt-8 pt-6 border-t border-white/20">
                      <Button variant="primary" onClick={() => currentNote._id ? handleUpdate(currentNote._id) : handleSave()} icon={<Sparkles size={18} />}>
                        {currentNote._id ? "Update Note" : "Save Note"}
                      </Button>
                      <Button variant="secondary" onClick={() => { setIsEditing(false); setCurrentNote({ id: null, title: "", content: "", attachments: [] }); setAttachments([]); }}
                        className="!bg-white/10 hover:!bg-white/20 !text-gray-200 !shadow-[0_4px_0_rgba(255,255,255,0.05)] active:!shadow-[0_2px_0_rgba(255,255,255,0.05)]">
                        Cancel
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {isLoading && !filteredNotes?.length ? (
                  [...Array(6)].map((_, i) => (
                    <Card key={i} className="animate-pulse !bg-black/30 !border-white/10">
                      <CardContent className="h-64">
                        <div className="h-6 bg-white/10 rounded w-3/4 mb-4"></div>
                        <div className="space-y-2">
                          <div className="h-4 bg-white/10 rounded w-full"></div>
                          <div className="h-4 bg-white/10 rounded w-5/6"></div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : filteredNotes?.length > 0 ? (
                  filteredNotes.map((note, index) => (
                    <Card key={note._id} expandable className="animate-fadeInUp group/note" style={{animationDelay: `${index * 0.05}s`}}>
                        <div className="absolute top-4 right-4 bg-black/40 text-gray-300 text-xs px-2.5 py-1 rounded-full backdrop-blur-sm border border-white/10 z-10">
                          {formatDate(note.createdAt)}
                        </div>
                      <CardContent className="pt-5 pb-4 relative">
                        <h2 className="text-xl font-semibold text-white mb-3 line-clamp-1 pr-20 group-hover/note:text-violet-300 transition-colors">
                          {note.title}
                        </h2>
                        <div className="relative h-40 overflow-hidden mb-3">
                          <div className="prose prose-xs dark:prose-invert max-w-none prose-p:text-gray-300 prose-headings:text-gray-200">
                            <div dangerouslySetInnerHTML={{__html: note.content.substring(0, 300) + (note.content.length > 300 ? '...' : '')}}/>
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black/40 dark:from-black/40 to-transparent via-black/30 dark:via-black/30" />
                        </div>
                        {note.attachments && note.attachments.length > 0 && (
                          <div className="flex items-center text-xs text-gray-400 group-hover/note:text-violet-400 transition-colors">
                            <FileTextIcon size={14} className="mr-1.5" /> {note.attachments.length} attachment{note.attachments.length === 1 ? '' : 's'}
                          </div>
                        )}
                        <div className="absolute bottom-4 right-4 flex space-x-1.5 opacity-0 group-hover/note:opacity-100 transition-opacity duration-200">
                          <IconButton icon={<Edit size={16} />} tooltip="Edit" onClick={(e)=>{e.stopPropagation();setCurrentNote(note);setIsEditing(true);setAttachments(note.attachments || []);}} variant="glass" size="sm"/>
                          <IconButton icon={<Trash2 size={16} />} tooltip="Delete" onClick={(e)=>{e.stopPropagation();handleDeleteNote(note._id);}} variant="glass" size="sm" className="hover:!bg-red-500/20 hover:!text-red-300"/>
                          <IconButton icon={<Maximize size={16} />} tooltip="Expand" onClick={(e)=>{e.stopPropagation();setExpandedCard(note._id);}} variant="glass" size="sm"/>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : searchQuery ? (
                  <div className="col-span-full"> <EmptyState title="No Matches" description={`Your search for "${searchQuery}" did not yield any results.`} icon={<Search />} action={<Button variant="secondary" onClick={() => setSearchQuery("")}>Clear Search</Button>} /> </div>
                ) : (
                  <div className="col-span-full"> <EmptyState title="No Notes Here" description="This notebook is empty. Create your first note to get started!" icon={<FileTextIcon />} action={<Button variant="primary" onClick={() => {setIsEditing(true);setCurrentNote({id: null, title: "", content: "", attachments: []});}} icon={<Plus size={18}/>}>Create Note</Button>} /> </div>
                )}
              </div>
            </>
          )}

          {expandedCard && (
            <div className="fixed inset-0 z-50 overflow-y-auto backdrop-blur-md flex items-center justify-center p-4 animate-fadeInUp" onClick={() => setExpandedCard(null)}>
              <div className="relative bg-gray-900/90 backdrop-blur-2xl border border-white/20 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col transform transition-all" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-4 border-b border-white/20 flex justify-between items-center sticky top-0 z-10 bg-gray-900/90 backdrop-blur-xl">
                  <h2 className="text-xl font-semibold text-white">{filteredNotes.find((n) => n._id === expandedCard)?.title}</h2>
                  <div className="flex space-x-2">
                    <IconButton icon={<Edit size={18} />} tooltip="Edit" variant="ghost" onClick={() => { const note = filteredNotes.find(n=>n._id === expandedCard); setCurrentNote(note); setIsEditing(true); setAttachments(note.attachments || []); setExpandedCard(null);}}/>
                    <IconButton icon={<X size={18} />} tooltip="Close" variant="ghost" onClick={() => setExpandedCard(null)}/>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
                  <NoteContent
                    noteId={expandedCard}
                    content={filteredNotes.find((n) => n._id === expandedCard)?.content}
                    attachments={filteredNotes.find((n) => n._id === expandedCard)?.attachments}
                    onDeleteAttachment={handleDeleteAttachment}
                  />
                </div>
                <div className="px-6 py-4 border-t border-white/20 sticky bottom-0 flex justify-between items-center bg-gray-900/90 backdrop-blur-xl">
                    <div className="text-xs text-gray-400 flex items-center space-x-3">
                        <span><Calendar size={13} className="inline mr-1"/> {formatDate(filteredNotes.find((n) => n._id === expandedCard)?.createdAt)}</span>
                        <span><Clock size={13} className="inline mr-1"/> {formatTime(filteredNotes.find((n) => n._id === expandedCard)?.updatedAt)}</span>
                    </div>
                  <Button variant="primary" onClick={() => setExpandedCard(null)} size="sm">Close</Button>
                </div>
              </div>
            </div>
          )}

          {showProfile && (
             <div className="fixed inset-0 z-50 overflow-hidden backdrop-blur-md flex items-start justify-end animate-fadeInRight" onClick={() => setShowProfile(false)}>
                <div 
                  ref={profilePanelRef}
                  className="relative bg-gray-900/90 backdrop-blur-2xl border-l border-white/20 shadow-2xl w-full max-w-md h-full flex flex-col transform transition-all overflow-hidden" 
                  onClick={(e) => e.stopPropagation()}
                >
                    <div className="px-6 py-5 border-b border-white/20 flex justify-between items-center sticky top-0 z-10 bg-gray-900/95 backdrop-blur-xl">
                        <h2 className="text-xl font-semibold text-white">User Profile</h2>
                        <IconButton icon={<X size={18} />} tooltip="Close" variant="ghost" onClick={() => setShowProfile(false)}/>
                    </div>
                    <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
                        <div className="p-6">
                            <div className="flex items-center space-x-4 mb-8">
                                <div className="relative">
                                    <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-white/10 shadow-xl">
                                        <img src={user?.imageUrl} alt={user?.fullName || "Profile"} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="absolute bottom-0 right-0 h-4 w-4 bg-green-500 rounded-full ring-2 ring-black/50 border border-white/20"></div>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white">{user?.fullName}</h3>
                                    <p className="text-gray-400">{user?.primaryEmailAddress?.emailAddress}</p>
                                </div>
                            </div>
                            
                            <div className="space-y-6">
                                <div className="bg-black/30 rounded-xl border border-white/10 p-4">
                                    <h4 className="text-sm font-semibold text-violet-300 mb-2 flex items-center">
                                        <Activity size={14} className="mr-2" /> ACCOUNT STATUS
                                    </h4>
                                    <div className="space-y-2">
                                        <div className="flex justify-between items-center py-2 border-b border-white/5">
                                            <span className="text-gray-300">Status</span>
                                            <span className="text-green-400 bg-green-500/10 px-2 py-1 rounded-full text-xs">Active</span>
                                        </div>
                                        <div className="flex justify-between items-center py-2 border-b border-white/5">
                                            <span className="text-gray-300">Plan</span>
                                            <span className="text-indigo-300">Premium</span>
                                        </div>
                                        <div className="flex justify-between items-center py-2">
                                            <span className="text-gray-300">Next Billing</span>
                                            <span className="text-gray-400">June 1, 2025</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="bg-black/30 rounded-xl border border-white/10 p-4">
                                    <h4 className="text-sm font-semibold text-violet-300 mb-2 flex items-center">
                                        <Layers size={14} className="mr-2" /> NOTEBOOK STATS
                                    </h4>
                                    <div className="grid grid-cols-2 gap-4 mt-3">
                                        <div className="bg-black/20 rounded-lg p-3 text-center border border-white/5">
                                            <p className="text-2xl font-bold text-white">{pages.length}</p>
                                            <p className="text-xs text-gray-400">Notebooks</p>
                                        </div>
                                        <div className="bg-black/20 rounded-lg p-3 text-center border border-white/5">
                                            <p className="text-2xl font-bold text-white">
                                                {pages.reduce((total, page) => total + (page.notes?.length || 0), 0)}
                                            </p>
                                            <p className="text-xs text-gray-400">Notes</p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="bg-black/30 rounded-xl border border-white/10 p-4">
                                    <h4 className="text-sm font-semibold text-violet-300 mb-2 flex items-center">
                                        <Users size={14} className="mr-2" /> USER SETTINGS
                                    </h4>
                                    <div className="space-y-4 mt-2">
                                        <div className="flex items-center justify-between">
                                            <label className="text-gray-300 flex items-center">
                                                <Moon size={16} className="mr-2" /> Dark Mode
                                            </label>
                                            <button 
                                                onClick={() => setIsDarkMode(!isDarkMode)}
                                                className={`w-12 h-6 rounded-full relative transition-colors duration-300 ${isDarkMode ? 'bg-violet-600' : 'bg-gray-600'}`}
                                            >
                                                <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-md transform transition-transform duration-300 ${isDarkMode ? 'left-7' : 'left-1'}`}></span>
                                            </button>
                                        </div>
                                        
                                        <div className="flex items-center justify-between">
                                            <label className="text-gray-300 flex items-center">
                                                <Lock size={16} className="mr-2" /> Private Notes
                                            </label>
                                            <button className="w-12 h-6 rounded-full bg-violet-600 relative">
                                                <span className="absolute top-1 left-7 w-4 h-4 rounded-full bg-white shadow-md"></span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="space-y-2">
                                    <Button variant="primary" className="w-full" icon={<User size={16} />}>
                                        Edit Profile
                                    </Button>
                                    <Button variant="glass" className="w-full" icon={<ExternalLink size={16} />}>
                                        Manage Subscription
                                    </Button>
                                    <Button variant="secondary" className="w-full !bg-white/10 hover:!bg-white/20 !text-gray-200 !shadow-[0_4px_0_rgba(255,255,255,0.05)] active:!shadow-[0_2px_0_rgba(255,255,255,0.05)]">
                                        Sign Out
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Chat Widget - Floating Button */}
      <div className="fixed bottom-5 right-5 z-30">
        <div className="flex flex-col items-end space-y-3">
          {/* Chat Widget Button */}
          <div className="group relative">
            <button
              onClick={() => setIsChatOpen(!isChatOpen)}
              className="bg-gradient-to-br from-violet-600 to-indigo-600 text-white p-3 rounded-full shadow-lg hover:shadow-violet-500/20 transition-all duration-300 flex items-center justify-center"
            >
              <div className="absolute -inset-0.5 bg-gradient-to-r from-violet-600 to-indigo-600 rounded-full blur opacity-0 group-hover:opacity-70 transition-opacity duration-300"></div>
              <div className="relative">
                <MessageCircle size={24} />
              </div>
            </button>
            <div className="absolute bottom-full mb-2 right-0 px-3 py-1.5 bg-black/80 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none transition-opacity duration-300 shadow-lg">
              Chat Assistant
            </div>
          </div>

          {/* Keyboard Shortcuts Button */}
          <div className="bg-black/60 dark:bg-black/80 backdrop-blur-md text-gray-300 px-3.5 py-2.5 rounded-xl text-xs flex items-center border border-white/20 shadow-lg">
            <Command size={14} className="mr-2 text-violet-400" />
            <span>Press <kbd className="bg-black/40 border border-white/20 px-1.5 py-0.5 rounded text-xs mx-1 text-violet-300">⌘K</kbd> for shortcuts</span>
          </div>
        </div>
      </div>

      {/* Chat Widget Container */}
      <div 
        className={`fixed bottom-20 right-5 w-80 h-96 bg-gray-900/95 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl overflow-hidden transition-all duration-300 transform z-30 ${isChatOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none'}`}
      >
        <div className="flex flex-col h-full">
          <div className="p-3 border-b border-white/20 bg-black/40 flex justify-between items-center">
            <h3 className="text-white font-medium flex items-center">
              <Sparkles size={16} className="mr-2 text-violet-400" />
              AI Assistant
            </h3>
            <button 
              onClick={() => setIsChatOpen(false)}
              className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-white/10 transition"
            >
              <X size={16} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
            {chatMessages.map((message, index) => (
              <div key={index} className={`mb-3 ${message.sender === 'user' ? 'text-right' : 'text-left'}`}>
                <div className={`inline-block rounded-xl px-4 py-2 max-w-[80%] ${
                  message.sender === 'user' 
                    ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white' 
                    : 'bg-black/40 border border-white/10 text-gray-200'
                }`}>
                  {message.text}
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-white/20 bg-black/40">
            <form onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }} className="relative">
              <input 
                ref={chatInputRef}
                type="text" 
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                placeholder="Type your message..." 
                className="w-full bg-black/40 border border-white/20 rounded-xl px-4 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50"
              />
              <button 
                type="submit"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-violet-400 hover:text-violet-300 p-1"
              >
                <Send size={16} />
              </button>
            </form>
          </div>
        </div>
      </div>
      
      <style jsx global>{`
        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
        }
        .text-gradient {
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-image: linear-gradient(to right, #a78bfa, #f472b6, #a78bfa); /* Lighter Violet, Pink */
          background-size: 200% auto;
          animation: text-shine 5s linear infinite;
        }
        @keyframes text-shine { to { background-position: 200% center; } }

        .dark .ql-toolbar {
          border-color: rgba(255, 255, 255, 0.2) !important;
          background-color: rgba(0,0,0,0.3) !important;
        }
        .dark .ql-container.ql-snow {
          border-color: rgba(255, 255, 255, 0.2) !important;
          color: #e5e7eb; /* gray-200 */
          background-color: rgba(0,0,0,0.2) !important;
        }
        .dark .ql-editor {
          color: #e5e7eb; /* gray-200 */
        }
        .dark .ql-snow .ql-stroke { stroke: #d1d5db; /* gray-300 */ }
        .dark .ql-snow .ql-fill, .dark .ql-snow .ql-stroke.ql-fill { fill: #d1d5db; /* gray-300 */ }
        .dark .ql-snow .ql-picker { color: #d1d5db; /* gray-300 */ }
        .dark .ql-snow .ql-picker-options { background-color: #1f2937; border-color: rgba(255,255,255,0.2); }
        
        .dark .ql-toolbar.ql-snow button:hover, .dark .ql-toolbar.ql-snow button.ql-active,
        .dark .ql-toolbar.ql-snow .ql-picker-label:hover, .dark .ql-toolbar.ql-snow .ql-picker-label.ql-active,
        .dark .ql-toolbar.ql-snow .ql-picker-item:hover, .dark .ql-toolbar.ql-snow .ql-picker-item.ql-selected {
          color: #a78bfa; /* lighter violet */
        }
        .dark .ql-toolbar.ql-snow button:hover .ql-stroke, .dark .ql-toolbar.ql-snow button.ql-active .ql-stroke {
          stroke: #a78bfa;
        }
        .dark .ql-editor.ql-blank::before {
            color: rgba(255, 255, 255, 0.4);
            font-style: normal;
        }
        .prose-container .ql-editor { padding: 1rem; }

        @keyframes fadeInUp { from { opacity:0; transform:translateY(15px); } to { opacity:1; transform:translateY(0); } }
        .animate-fadeInUp { animation:fadeInUp 0.5s ease-out forwards; }
        
        @keyframes fadeInRight { from { opacity:0; transform:translateX(30px); } to { opacity:1; transform:translateX(0); } }
        .animate-fadeInRight { animation:fadeInRight 0.4s ease-out forwards; }
        
        @keyframes pulse-gentle { 0%,100%{opacity:0.7;} 50%{opacity:1;} }
        .animate-pulse-gentle { animation:pulse-gentle 3s infinite; }

        /* Custom scrollbar for modern browsers */
        .scrollbar-thin::-webkit-scrollbar { width: 6px; height: 6px; }
        .scrollbar-thin::-webkit-scrollbar-track { background: transparent; }
        .scrollbar-thin::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 3px; }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.3); }
        .dark .scrollbar-thin::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); }
        .dark .scrollbar-thin::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.25); }

        /* For Firefox */
        .scrollbar-thin { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.2) transparent; }
        .dark .scrollbar-thin { scrollbar-color: rgba(255,255,255,0.15) transparent; }
      `}</style>
    </div>
  );
};

export default NoteApp;